import { AxiosResponse, AxiosInstance } from 'axios';
import VehicleDetail, { VehicleModelDetail, YearModel } from '@/models/VehicleDetail';
import { Dictionary } from 'vue-router/types/router';
import { UserModel } from '@/stores/StoreVuexModule';
import { KeyValuePair } from '@/models/KeyValuePair';

const imageCache: Dictionary<string> = {};

export default class VehicleService {
    private axiosInstance: AxiosInstance;

    public constructor(axiosInstance: AxiosInstance) {
        this.axiosInstance = axiosInstance;
    }

    public async GetVehicleByRegNo(regNo: string): Promise<VehicleDetail | null | string> {
        regNo = this.getRegistrationNumber(regNo);

        // tslint:disable-next-line: max-line-length
        const res: void | AxiosResponse<VehicleDetail> = await this.axiosInstance.get<VehicleDetail>(`Vehicle/search/${regNo}`);
        return res.data;
    }

    public async GetMakes(apiKey: string): Promise<Array<KeyValuePair<number, string>>> {
        const res: AxiosResponse<Array<KeyValuePair<number, string>>> =
            await this.axiosInstance.post<Array<KeyValuePair<number, string>>>('Vehicle/makes', [4], {
                headers: {
                    'x-IsAnonymous': '',
                    'x-ApiKey': apiKey,
                },
            });
        return res.data;
    }

    public async GetModelGroups(vehicleDetail: VehicleDetail): Promise<Array<KeyValuePair<number, string>>> {
        const url: string = `Vehicle/${vehicleDetail.makeId}/modelGroups`;

        const res: AxiosResponse<Array<KeyValuePair<number, string>>> =
            await this.axiosInstance.post<Array<KeyValuePair<number, string>>>(url, [4], {
                headers: {
                    'x-IsAnonymous': '',
                },
            });
        return res.data;
    }

    public async GetModels(vehicleDetail: VehicleDetail): Promise<VehicleModelDetail[]> {
        const url: string = `Vehicle/${vehicleDetail.makeId}/${vehicleDetail.modelGroupId}/models`;

        const res: AxiosResponse<VehicleModelDetail[]> = await this.axiosInstance.post<VehicleModelDetail[]>(url, [4], {
            headers: {
                'x-IsAnonymous': '',
            },
        });
        return res.data;
    }

    public async GetYears(vehicleDetail: VehicleDetail): Promise<YearModel[]> {
        const url: string = `Vehicle/${vehicleDetail.modelId}/years`;

        const res: AxiosResponse<YearModel[]> = await this.axiosInstance.post<YearModel[]>(url, undefined, {
            headers: {
                'x-IsAnonymous': '',
            },
        });
        return res.data;
    }

    public async GetSeats(vehicleDetail: VehicleDetail): Promise<number[]> {
        const url: string =
            `Vehicle/${vehicleDetail.makeId}/${vehicleDetail.modelGroupId}/${vehicleDetail.modelId}/seatCounts`;

        // tslint:disable-next-line: max-line-length
        const res: AxiosResponse<number[]> = await this.axiosInstance.post<number[]>(url, [4], {
            params: vehicleDetail.yearModel, headers: {
                'x-IsAnonymous': '',
            },
        });
        return res.data;
    }

    public async GetTransmissions(vehicleDetail: VehicleDetail): Promise<Array<KeyValuePair<number, string>>> {
        // tslint:disable-next-line:max-line-length
        const url: string = `Vehicle/${vehicleDetail.makeId}/${vehicleDetail.modelGroupId}/${vehicleDetail.modelId}/transmissions`;

        // tslint:disable-next-line: max-line-length
        const res: AxiosResponse<Array<KeyValuePair<number, string>>> = await this.axiosInstance.post<Array<KeyValuePair<number, string>>>(url, [4], {
            params: vehicleDetail.yearModel, headers: {
                'x-IsAnonymous': '',
            },
        });
        return res.data;
    }

    public async GetImageByRegNo(regNo: string): Promise<string | null> {
        regNo = this.getRegistrationNumber(regNo);
        if (regNo === '') {
            return null;
        }

        if (imageCache[regNo]) {
            return imageCache[regNo];
        }

        const res: void | AxiosResponse<string> = await this.axiosInstance.get<string>(`Vehicle/image/${regNo}`);

        imageCache[regNo] = res.data;

        return res.data;
    }

    public async GetImageByDetail(vehicleDetail: VehicleDetail): Promise<string | null> {
        if (imageCache[vehicleDetail.asKey]) {
            return imageCache[vehicleDetail.asKey];
        }

        const url: string = `Vehicle/${vehicleDetail.make}/${vehicleDetail.modelGroup}/${vehicleDetail.model}/image`;
        // tslint:disable-next-line: max-line-length
        const res: void | AxiosResponse<string> = await this.axiosInstance.get<string>(url, {
            params: vehicleDetail.yearModel, headers: {
                'x-IsAnonymous': '',
            },
        });

        imageCache[vehicleDetail.asKey] = res.data;

        return res.data;
    }

    private getRegistrationNumber(regNo: string) {
        return (regNo || '').replace(' ', '');
    }
}
